import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099GInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-G instructions for Tax Year 2025"
          description="Learn how to fill out Form 1099-G for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report government payments and refunds."
          keywords=""
          canonical="resources/information-returns/form-1099-g-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-G</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-g-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-G at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-G{' '}
                        </strong>{' '}
                        is an informational tax form used by federal, state, and
                        local government agencies to report certain government
                        payments that may be taxable to the recipient, including
                        unemployment compensation, state or local income tax
                        refunds, taxable grants, RTAA payments, agricultural
                        payments, and other qualified government payments.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        if filing electronically.{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          line-by-line instructions{' '}
                        </strong>{' '}
                        for payer and recipient information, account numbers,
                        second TIN notices, unemployment compensation,
                        state/local tax refunds, tax year details, federal tax
                        withholding, RTAA payments, taxable grants, agricultural
                        payments, trade or business income, market gain, and
                        state information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late or incorrect filing penalties range from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per form
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-G?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1099-G is an informational tax form used to report
                  certain types of government payments. These payments may
                  include:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Unemployment compensation</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      State or local income tax refunds
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Taxable grants</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Other types of government payments
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The form provides the recipient and the IRS with information
                  about the amount of these payments, which may be taxable and
                  need to be reported on the recipient&apos;s tax return.
                </Typography>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-G?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Entities, including federal, state, or local governments, need
                  to file Form 1099-G if they have made certain types of
                  payments. These payments include:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Unemployment compensation</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      State or local income tax refunds, credits, or offsets
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Reemployment trade adjustment assistance (RTAA) payments
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Taxable grants</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Agricultural payments</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The form must be filed to report these payments to the IRS and
                  the recipients.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-G due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-G:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-G to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-G with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-G deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099ginstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-G with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must deliver copies of Form 1099-G to recipients by
                  January 31st of the year following the relevant tax year. This
                  allows recipients to accurately file their tax returns on
                  time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> *if the due date falls on the weekend or federal
                  holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-G can lead to IRS penalties. To avoid potential
                  penalties, payers should take steps to ensure that Form 1099-G
                  is filed and distributed both accurately and on time.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-G penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-G
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-G penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-G forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099ginstructions`}
                    >
                      E-file Form 1099-G
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-G Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-G.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-1099-g-payer-details-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-payer-details-img.png" />
                  <img
                    src="/assets/images/form-1099-g-payer-details-img.png"
                    loading="lazy"
                    alt="Form 1099-g-payer details"
                    width={591}
                    height={233}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business)
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-recipient-details-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-recipient-details-img.png" />
                  <img
                    src="/assets/images/form-1099-g-recipient-details-img.png"
                    loading="lazy"
                    alt="Form 1099-g-recipient details"
                    width={591}
                    height={320}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b> Account number:</b> You must provide the account number
                    if you have multiple accounts for the same recipient and
                    filing more than one Form 1099-G for that recipient.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    <b> 2nd TIN not:</b> If the IRS has notified you two times
                    within the last 3 years that the payee provided an incorrect
                    TIN, you can enter an &quot;X&quot; in this box. By doing
                    so, the IRS will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Unemployment Compensation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-unemployment-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-unemployment-img.png" />
                  <img
                    src="/assets/images/form-1099-g-unemployment-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-1-unemployemnt compensation"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any unemployment compensation payments of $10 or more in
                  this box. This includes payments from:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Unemployment benefits</li>
                  <li style={{ marginBottom: 12 }}>
                    Railroad Retirement Board unemployment payments
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Payments from contributory programs considered unemployment
                    compensation (like California&apos;s Family Temporary
                    Disability Insurance or governmental paid family leave
                    programs)
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the total payment amount before any federal income tax
                  withholding.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: State or Local Income Tax Refunds, Credits, or Offsets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-state-local-tax-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-state-local-tax-img.png" />
                  <img
                    src="/assets/images/form-1099-g-state-local-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-2-state or local income tax refunds, credits, or offsets"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any of the following payments of $10 or more in this
                  box:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Refunds of state or local income tax
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Credits for state or local income tax overpayments
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Offsets/reductions of state or local income tax owed
                  </li>
                </ul>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  This includes:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Refunds or carry-forward credits from refundable state tax
                    credits
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Incentive payments made under existing state tax laws and
                    administered by the state tax agency
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Box 2 Amount is for tax year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-amount-tax-year-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-amount-tax-year-img.png" />
                  <img
                    src="/assets/images/form-1099-g-amount-tax-year-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-3-box 2 amount is for tax year"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <ul style={{ marginBottom: 10 }}>
                  <li style={{ marginBottom: 12 }}>
                    If the amount in Box 2 is for the current tax year, leave
                    Box 3 blank.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If the amount in Box 2 is for a previous tax year, enter the
                    tax year it applies to in Box 3 in the “YYYY” format.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are reporting amounts from Box 2 that are for multiple
                  tax years, file a separate Form 1099-G for each tax year,
                  showing only the amount for that specific year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-federal-tax-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-federal-tax-img.png" />
                  <img
                    src="/assets/images/form-1099-g-federal-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-4-federal income tax withheld"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any federal income tax withheld from payments reported.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Withholding is required if recipients have not
                  provided their TIN.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: RTAA Payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-rtaa-payments-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-rtaa-payments-img.png" />
                  <img
                    src="/assets/images/form-1099-g-rtaa-payments-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-5-rtaa payments"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter RTAA payments of $600 or more that you paid to eligible
                  individuals under the Reemployment Trade Adjustment Assistance
                  program.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Taxable Grants
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-taxable-grants-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-taxable-grants-img.png" />
                  <img
                    src="/assets/images/form-1099-g-taxable-grants-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-6-taxable grants"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the following taxable grant amounts in this box:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 10 }}>
                    Grants from federal, state, or local programs providing
                    energy financing or grants for U.S. energy/conservation
                    projects
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    Taxable grants from Indian tribal governments
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    Other taxable grants of $600 or more
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 10 }}>
                    State and local grants (typically taxable for federal
                    income)
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    Federal grants, unless the legislation specifically makes
                    them non-taxable
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT report scholarships or fellowships in this box.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Agriculture Payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-agriculture-payment-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-agriculture-payment-img.png" />
                  <img
                    src="/assets/images/form-1099-g-agriculture-payment-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-7-agriculture payments"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount of agricultural subsidy payments you
                  received from the USDA (U.S. Department of Agriculture) during
                  the year. This includes:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 10 }}>
                    Market facilitation program payments
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    Any other USDA agricultural subsidy payments
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you received subsidy payments on behalf of someone else (as
                  a nominee),{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-G
                  </Link>
                  .
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Trade or Business Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-trade-business-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-trade-business-img.png" />
                  <img
                    src="/assets/images/form-1099-g-trade-business-img.png"
                    loading="lazy"
                    alt="Form 1099-g-box-8-check if box 2 is trade or business income"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if the Box 2 amount is for a tax refund/credit
                  specifically related to business income, not personal income.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Market Gain
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-market-gain-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-market-gain-img.png" />
                  <img
                    src="/assets/images/form-1099-g-market-gain-img.png"
                    loading="lazy"
                    alt="Form 1099-g- box-9-market gain"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report market gain associated with the repayment of a CCC
                  loan, whether repaid using cash or CCC certificates.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10 and 11: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-g-state-information-img.webp" />
                  <source srcSet="/assets/images/form-1099-g-state-information-img.png" />
                  <img
                    src="/assets/images/form-1099-g-state-information-img.png"
                    loading="lazy"
                    alt="Form 1099-g- box-10 and 11-state information"
                    width={487}
                    height={90}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 10a:</b> Enter the 2-letter state abbreviation for the
                  state
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 10b:</b> Enter the state identification number
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 11:</b> Report any state income tax withheld from the
                  payment
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-G
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can file Form 1099-G, either electronically or by mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the IRS-recommended method due to its efficiency,
                  accuracy, and quick processing notifications.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-G
                  </Link>{' '}
                  which you can complete within minutes. Her’s how it works:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information, including:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Payer&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Recipient&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Payment details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review all entered information for accuracy.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form securely to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Provide a copy to the recipient.
                  </li>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it. Here are the steps for paper filing:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps for paper filing:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-G from the official IRS website
                    (www.irs.gov).
                  </li>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission. You can order these from the IRS or approved
                    vendors.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields using black ink. This includes:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Recipient&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Payment details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The address varies based on
                    your state. Refer to the Where to send{' '}
                    <Link
                      href="#1099-g-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-G - Mailing address
                    </Link>{' '}
                    section to get the address for your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by February 15th of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                >
                  <ReportProblemRoundedIcon
                    sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                  />{' '}
                  Important considerations for paper filing
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Mail forms several weeks before the deadline to ensure
                    timely arrival.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Use certified mail for proof of timely filing.
                  </li>
                  <li style={{ marginBottom: 0 }}>
                    Ensure all copies are legible.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                    id="1099-g-mailing-address"
                  >
                    file Form 1099-G electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-G - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-G varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-G:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-G to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-G with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-G
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here’s what you need
                  to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-G:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Business details</li>
                  <li style={{ marginBottom: 16 }}>Recipient details</li>
                  <li style={{ marginBottom: 16 }}>
                    Unemployment compensation
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    State or local income tax refunds, credits, or offsets
                  </li>
                  <li style={{ marginBottom: 16 }}>RTAA payments</li>
                  <li style={{ marginBottom: 16 }}>Taxable grants</li>
                  <li style={{ marginBottom: 16 }}>Agriculture payments</li>
                  <li style={{ marginBottom: 16 }}>Market gain</li>
                  <li style={{ marginBottom: 16 }}>
                    Federal and state income tax withheld
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-G using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mt: 1.5, mb: 2 }}>
                  <b>Step 1: Complete Form 1099-G</b>
                  <br />
                  Open Form 1099-G and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-G to
                  the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-G to the recipient.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-G with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-g/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-G
                  </Link>{' '}
                  with TaxZerone offers several advantages:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS Form Validations:{' '}
                      </span>{' '}
                      TaxZerone&apos;s system checks your returns for errors or
                      missing information, ensuring accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Email Recipient Copies:
                      </span>{' '}
                      Conveniently send recipient copies of Form 1099-G via
                      email, eliminating the need for manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Secure Portal Access (ZeroneVault):
                      </span>{' '}
                      Recipients can access their return copies anytime,
                      anywhere through the secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Competitive Pricing:{' '}
                      </span>{' '}
                      TaxZerone offers industry-leading pricing, with rates
                      starting as low as{' '}
                      <span style={{ fontWeight: 600 }}>$0.59</span> per form,
                      making it affordable for businesses of all sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By using TaxZerone&apos;s e-filing solution, you can
                  streamline your Form 1099-G filing, save time, reduce errors,
                  and provide a better experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-G.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099ginstructions`}
              >
                E-file Form 1099-G
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099GInstructions;
